import { Outlet, NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import BottomNav from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import {
  Home, History, Users, TrendingUp, Settings as SettingsIcon, Plus, Camera,
  Search, Eye, Wallet, LogOut, Spade, Menu,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NavLink as RouterNavLink } from "react-router-dom";
import { CommandPalette } from "@/ui/v2/CommandPalette";
import { InstallPrompt } from "@/components/InstallPrompt";
import { useViewMode } from "@/lib/view-mode-context";
import { useAuth } from "@/lib/auth";
import { useMemo } from "react";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";

const PAGE_TITLES: Record<string, { title: string; subtitle: string }> = {
  "/": { title: "Dashboard", subtitle: "Overview & standings" },
  "/sessions": { title: "Sessions", subtitle: "History & results" },
  "/players": { title: "Players", subtitle: "Roster & stats" },
  "/expenses": { title: "Expenses", subtitle: "Shared costs" },
  "/statistics": { title: "Statistics", subtitle: "Leaderboards & trends" },
  "/settings": { title: "Settings", subtitle: "Preferences" },
  "/settlement": { title: "Settlement", subtitle: "Settle up" },
  "/who-pays-who": { title: "Who Pays Who", subtitle: "Settlement calculator" },
};

export function ModernShell() {
  const isMobile = useIsMobile();
  useKeyboardShortcuts();
  const { isPublic, targetUserId } = useViewMode();
  const { user, signOut } = useAuth();
  const location = useLocation();

  const userInitials = useMemo(() => {
    if (!user) return "?";
    const meta = user.user_metadata;
    if (meta?.name) return (meta.name as string).charAt(0).toUpperCase();
    if (meta?.full_name) return (meta.full_name as string).charAt(0).toUpperCase();
    if (user.email) return user.email.charAt(0).toUpperCase();
    return "?";
  }, [user]);

  const getPath = (base: string) => {
    if (!isPublic) return base;
    if (base === "/") return `/public/${targetUserId}`;
    return `/public/${targetUserId}${base}`;
  };

  const nav = [
    { to: getPath("/"), label: "Home", icon: Home, end: true },
    { to: getPath("/sessions"), label: "Sessions", icon: History },
    { to: getPath("/players"), label: "Players", icon: Users },
    { to: getPath("/expenses"), label: "Expenses", icon: Wallet },
    { to: getPath("/statistics"), label: "Stats", icon: TrendingUp },
    ...(!isPublic ? [{ to: "/settings", label: "Settings", icon: SettingsIcon }] : []),
  ];

  const currentPath = isPublic
    ? location.pathname.replace(`/public/${targetUserId}`, "") || "/"
    : location.pathname;
  const pageInfo = Object.entries(PAGE_TITLES).find(([path]) =>
    path === "/" ? currentPath === "/" : currentPath.startsWith(path),
  )?.[1] ?? { title: "SSC Score", subtitle: "" };

  return (
    <SidebarProvider defaultOpen>
      {/* ── Sidebar ─────────────────────────────────────────── */}
      <Sidebar variant="inset" collapsible="icon">
        <SidebarHeader className="gap-3 pt-4">
          {/* Brand */}
          <div className="flex items-center justify-between gap-2 px-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center shrink-0 shadow-card">
                <Spade className="h-4 w-4 text-primary-foreground fill-primary-foreground" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold tracking-tight truncate">SSC Score</div>
                <div className="text-[10px] text-sidebar-foreground/55 truncate font-medium">
                  {isPublic ? "Public View" : "Score Tracker"}
                </div>
              </div>
            </div>
            <SidebarTrigger className="md:flex shrink-0 h-7 w-7 text-sidebar-foreground/50 hover:text-sidebar-foreground" />
          </div>

          {/* Action buttons */}
          {!isPublic && (
            <div className="grid grid-cols-2 gap-2 px-3">
              <NavLink to="/session/new">
                <Button size="sm" className="w-full gap-1.5 h-9 text-xs font-semibold">
                  <Plus className="h-3.5 w-3.5" />
                  New
                </Button>
              </NavLink>
              <NavLink to="/session/import">
                <Button size="sm" variant="secondary" className="w-full gap-1.5 h-9 text-xs font-semibold">
                  <Camera className="h-3.5 w-3.5" />
                  Import
                </Button>
              </NavLink>
            </div>
          )}

          {isPublic && (
            <div className="px-3">
              <Badge
                variant="outline"
                className="w-full justify-center gap-1.5 py-1.5 border-primary/25 bg-primary/6 text-primary text-[10px]"
              >
                <Eye className="h-3 w-3" />
                Read-only View
              </Badge>
            </div>
          )}
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {nav.map((n) => (
                  <SidebarMenuItem key={n.to}>
                    <SidebarMenuButton asChild tooltip={n.label}>
                      <NavLink
                        to={n.to}
                        end={n.to === "/" || (isPublic && n.to === `/public/${targetUserId}`)}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 text-sm transition-colors",
                            isActive
                              ? "font-semibold text-sidebar-primary"
                              : "text-sidebar-foreground/70 hover:text-sidebar-foreground",
                          )
                        }
                      >
                        <n.icon className="h-4 w-4 shrink-0" />
                        <span>{n.label}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="px-3 pb-3">
          {!isPublic && user && (
            <div className="flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-sidebar-accent transition-colors">
              <div
                title={user.email || ""}
                className="w-7 h-7 rounded-full bg-primary/15 border border-primary/25 flex items-center justify-center text-[11px] font-bold text-primary shrink-0"
              >
                {userInitials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-sidebar-foreground/60 truncate">{user.email}</div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 shrink-0 text-sidebar-foreground/50 hover:text-sidebar-foreground"
                title="Sign out"
                onClick={() => signOut()}
              >
                <LogOut className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          {!isPublic && (
            <div className="text-[10px] text-sidebar-foreground/40 px-2 pt-1">
              ⌘K — Command palette
            </div>
          )}
        </SidebarFooter>
      </Sidebar>

      {/* ── Main content ─────────────────────────────────────── */}
      <SidebarInset>
        <div className={cn("min-h-svh", isMobile && "pb-20 pb-sticky-footer")}>
          {/* Sticky top header */}
          <header className="sticky top-0 z-20 border-b border-border/60 glass">
            <div className="flex items-center gap-3 px-4 py-3">
              <SidebarTrigger className="md:hidden h-8 w-8 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold leading-tight truncate">{pageInfo.title}</div>
                {pageInfo.subtitle && (
                  <div className="text-[11px] text-muted-foreground/70 truncate leading-tight">
                    {pageInfo.subtitle}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <InstallPrompt />
                {!isPublic && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    title="Command palette (⌘K)"
                    onClick={() => {
                      const ev = new KeyboardEvent("keydown", { key: "k", ctrlKey: true });
                      window.dispatchEvent(ev);
                    }}
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                )}
                {!isPublic && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        title="Menu"
                      >
                        <Menu className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem asChild>
                        <RouterNavLink to="/settings" className="flex items-center gap-2 w-full">
                          <SettingsIcon className="h-4 w-4" />
                          Settings
                        </RouterNavLink>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive gap-2"
                        onClick={() => signOut()}
                      >
                        <LogOut className="h-4 w-4" />
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </header>

          <main className="px-4 py-6 animate-fade-in">
            <Outlet />
          </main>
        </div>

        {isMobile && <BottomNav />}
        {!isPublic && <CommandPalette />}
      </SidebarInset>
    </SidebarProvider>
  );
}
